// Typed client for the submit-provider-application Edge Function.
//
// The endpoint stays verify_jwt = false (the Supabase gateway still needs the
// publishable key to route at all), but since WP2 the console only calls it from
// a signed-in #/apply. We send the user's ACCESS TOKEN as the bearer, and the
// Edge Function resolves auth.uid() from it and stores that on the application.
// That is what stops the old crypto.randomUUID() minting: the approved profile is
// keyed to a real auth user who can actually sign in to it.
//
// If no session is passed we fall back to the publishable key, which the Edge
// Function correctly reads as "anonymous" and records with user_id NULL — an
// unclaimed, claimable profile rather than an orphan.

const FUNCTIONS_BASE = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1`;

// [value, human label] — the 13 valid provider type values. Sent on the wire as
// `business_type` and mapped to the real `provider_type` column server-side by
// the submit-provider-application Edge Function. Default-select nothing so the
// applicant must choose.
export const PROVIDER_TYPE_OPTIONS: ReadonlyArray<readonly [string, string]> = [
  ['restaurant', 'Restaurant'],
  ['bar', 'Bar'],
  ['food_drink', 'Food & drink (deli, café, gelateria, bakery)'],
  ['venue', 'Venue or event space'],
  ['activity', 'Activity provider (cooking class, workshop, etc.)'],
  ['walking_tours', 'Walking tours'],
  ['bike_tours', 'Bike tours'],
  ['food_tours', 'Food tours'],
  ['wine_tours', 'Wine tours'],
  ['mechanic', 'Bike shop / mechanic'],
  ['mobile_mechanic', 'Mobile bike mechanic'],
  ['influencer', 'Influencer / content creator'],
  ['other', 'Other'],
];

export interface ApplicationPayload {
  business_name: string;
  business_type: string;
  description?: string | null;
  contact_email: string;
  contact_phone?: string | null;
  website?: string | null;
  instagram_handle?: string | null;
  instagram_follower_count?: number | null;
  facebook_handle?: string | null;
  tiktok_handle?: string | null;
  address?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  city?: string | null;
  country?: string | null;
  profile_image_url?: string | null;
  experience?: string | null;
  why_join?: string | null;
  turnstile_token?: string | null;
}

export async function submitApplication(
  payload: ApplicationPayload,
  accessToken?: string,
): Promise<{ applicationId: string }> {
  // The Edge Function is public (verify_jwt=false), but the Supabase gateway
  // still requires the publishable anon key as `apikey` to route the request.
  const anon = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
  const bearer = accessToken ?? anon;
  let resp: Response;
  try {
    resp = await fetch(`${FUNCTIONS_BASE}/submit-provider-application`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        apikey: anon,
        Authorization: `Bearer ${bearer}`,
      },
      body: JSON.stringify(payload),
    });
  } catch {
    throw new Error('Could not reach the server. Check your connection and try again.');
  }
  const body = (await resp.json().catch(() => ({}))) as { applicationId?: string; error?: string };
  if (!resp.ok || !body.applicationId) {
    throw new Error(body.error ?? `Submission failed (${resp.status}).`);
  }
  return { applicationId: body.applicationId };
}
